import React, { useRef, lazy, Suspense } from 'react'
import PropTypes from 'prop-types'
import { useUser, useIsHelping, useUserProfile } from '@fs/zion-user'
import { isFSDomain } from '@fs/zion-axios-base'
import { useNonReligiousMode } from '@fs/zion-permissions'
import Wrapper from './shared/components/Wrapper'
import useHeightAdjustment from './shared/hooks/useHeightAdjustment'
import { needsBaseUrlPrefix } from './shared/needsBaseUrlPrefix'

const HelperMode = lazy(() => import('@fs/zion-helper-mode'))
const LazyHeader = lazy(() => import('@fs/zion-header'))
const LazyLogoOnlyHeader = lazy(() => import('@fs/zion-header').then((mod) => ({ default: mod.LogoOnlyHeader })))
const AnalyticsPageView = lazy(() => import('./helpers/AnalyticsPageView'))

export default function HeaderApp({
  mockFlags,
  mockActivities,
  pageDetail,
  pageChannel,
  logoOnlyHeader,
  signOutReturnUrl,
}) {
  const { userLoading, signedIn, ...user } = useUser()
  const { userProfile } = useUserProfile()
  const isHelping = useIsHelping()
  const ref = useRef(null)
  useHeightAdjustment(ref)

  const fsDomain = isFSDomain(window.location.href)
  const [nonReligiousModeCheck, status, error] = useNonReligiousMode()
  const nonReligiousMode = status === 'loading' || error !== null || nonReligiousModeCheck
  const needsBaseUrl = needsBaseUrlPrefix()

  const config = {
    ...user,
    signedIn: signedIn && !userLoading,
    profileName: userProfile?.name.value,
    showChat: fsDomain, // show chat only on FS domain
    redirectNotifications: needsBaseUrl, // redirect to FS notifications page
    external: needsBaseUrl, // links need prepending even if on FS subdomain
    useBaseUrl: needsBaseUrl, // logo url
    signOutReturnUrl,
    nonReligiousMode,
  }

  // todo does this need outage banner
  // todo does this need skip to content
  return (
    // add z-index so poppers don't go under page content
    <div ref={ref} style={{ zIndex: 1000, position: 'relative' }}>
      <Wrapper mockFlags={mockFlags} mockActivities={mockActivities}>
        {isHelping && (
          <Suspense fallback={null}>
            <HelperMode />
          </Suspense>
        )}
        <Suspense fallback={null}>{logoOnlyHeader ? <LazyLogoOnlyHeader /> : <LazyHeader config={config} />}</Suspense>
        {pageDetail || pageChannel ? (
          <Suspense fallback={null}>
            <AnalyticsPageView pageDetail={pageDetail} pageChannel={pageChannel} />
          </Suspense>
        ) : null}
      </Wrapper>
    </div>
  )
}

HeaderApp.propTypes = {
  mockFlags: PropTypes.objectOf(
    PropTypes.shape({
      treatment: PropTypes.bool,
    })
  ),
  mockActivities: PropTypes.objectOf(
    PropTypes.shape({
      experienceId: PropTypes.string,
      color: PropTypes.string,
      buttonEmphasis: PropTypes.string,
    })
  ),
  pageDetail: PropTypes.string,
  pageChannel: PropTypes.string,
  logoOnlyHeader: PropTypes.bool,
  signOutReturnUrl: PropTypes.string,
}
